import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Năng lượng trong dao động điều hoà (Lớp 11 — Bài 3):
// Wt = ½k·x², Wđ = ½m·v², W = Wt + Wđ = ½k·A² = const. Quan sát chuyển hoá Wđ ↔ Wt.
const EnergyOscillationSimulation: React.FC<Props> = ({
  title = 'Năng lượng trong dao động điều hoà',
  description = 'Cơ năng W = Wđ + Wt không đổi. Tại biên: toàn thế năng; tại VTCB: toàn động năng.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const tRef = useRef(0)
  const [running, setRunning] = useState(true)
  const [A, setA] = useState(1)      // biên độ (chuẩn hoá)
  const [omega, setOmega] = useState(2)
  const phaseRef = useRef(0)         // pha ban đầu (đặt khi kéo vật)
  const firstRef = useRef(false)
  const W = 680, H = 280
  const XC = 200, AMP = 90           // tâm + biên độ vẽ (px)

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const t = tRef.current
    const ph = omega * t + phaseRef.current
    const cosx = Math.cos(ph), sinx = Math.sin(ph)
    const Wtot = 1 // chuẩn hoá ½kA² = 1
    const Wt = Wtot * cosx * cosx
    const Wd = Wtot * sinx * sinx

    // ── Con lắc lò xo NẰM NGANG (trái) — đồng bộ với bài "kéo & thả" ──
    const cy = 110, half = 22, wallX = 56
    const mx = XC + A * cosx * AMP
    // Mặt sàn (mặt phẳng ngang) + tường
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(0, cy + half, 400, 6)
    ctx.fillStyle = '#94a3b8'; ctx.fillRect(wallX - 10, cy - half - 12, 10, half * 2 + 24)
    // VTCB
    ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(XC, cy - half - 16); ctx.lineTo(XC, cy + half + 6); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#94a3b8'; ctx.font = '11px sans-serif'; ctx.fillText('VTCB', XC - 14, cy - half - 20)
    // Lò xo (tường → vật)
    const x1 = wallX, x2 = mx - half, coils = 14
    ctx.strokeStyle = '#64748b'; ctx.lineWidth = 2.5; ctx.beginPath(); ctx.moveTo(x1, cy)
    for (let i = 1; i < coils; i++) { const t2 = i / coils; const xx = x1 + (x2 - x1) * t2; const yy = cy + (i % 2 ? -12 : 12); ctx.lineTo(xx, yy) }
    ctx.lineTo(x2, cy); ctx.stroke()
    // Vật (hình vuông gradient — giống bài kéo & thả)
    const g = ctx.createLinearGradient(0, cy - half, 0, cy + half); g.addColorStop(0, '#7dd3fc'); g.addColorStop(1, '#0284c7')
    ctx.fillStyle = g; ctx.fillRect(mx - half, cy - half, half * 2, half * 2)
    ctx.strokeStyle = '#075985'; ctx.lineWidth = 2; ctx.strokeRect(mx - half, cy - half, half * 2, half * 2)

    // ── Cột năng lượng (phải) ──
    const bx = 430, bw = 60, bh = 180, by = 220, gap = 80
    const bar = (x: number, val: number, color: string, label: string) => {
      ctx.fillStyle = '#e2e8f0'; ctx.fillRect(x, by - bh, bw, bh)
      ctx.fillStyle = color; ctx.fillRect(x, by - bh * val, bw, bh * val)
      ctx.strokeStyle = '#94a3b8'; ctx.strokeRect(x, by - bh, bw, bh)
      ctx.fillStyle = '#334155'; ctx.font = 'bold 12px sans-serif'; ctx.textAlign = 'center'
      ctx.fillText(label, x + bw / 2, by + 16); ctx.fillText(`${Math.round(val * 100)}%`, x + bw / 2, by - bh * val - 6); ctx.textAlign = 'left'
    }
    bar(bx, Wd, '#10b981', 'Wđ')
    bar(bx + gap, Wt, '#3b82f6', 'Wt')
    bar(bx + gap * 2, Wd + Wt, '#f59e0b', 'W')
  }, [A, omega])

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    tRef.current += dt; draw(); raf.current = requestAnimationFrame(tick)
  }, [draw])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { draw() }, [draw])

  // ── Kéo vật để đặt biên độ/vị trí rồi thả ──
  const [, bump] = useState(0)
  const dragRef = useRef(false)
  const toX = (e: React.PointerEvent) => { const r = cv.current!.getBoundingClientRect(); return (e.clientX - r.left) / r.width * W }
  const curMx = () => XC + A * Math.cos(omega * tRef.current + phaseRef.current) * AMP
  const onDown = (e: React.PointerEvent) => {
    if (Math.abs(toX(e) - curMx()) < 28) {
      dragRef.current = true; setRunning(false)
      if (!firstRef.current) { firstRef.current = true; onComplete?.() }
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    let xd = (toX(e) - XC) / AMP; xd = Math.max(-1, Math.min(1, xd))
    phaseRef.current = xd >= 0 ? 0 : Math.PI; tRef.current = 0
    setA(Math.max(0.2, Math.abs(xd))); bump((n) => n + 1)
  }
  const onUp = () => { if (dragRef.current) { dragRef.current = false; setRunning(true) } }

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 my-3">
      <h4 className="font-bold text-emerald-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="space-y-2 mt-3">
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Biên độ A</span>
          <input type="range" min={0.4} max={1} step={0.1} value={A} onChange={(e) => setA(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{A.toFixed(1)}</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Tần số góc ω</span>
          <input type="range" min={1} max={4} step={0.5} value={omega} onChange={(e) => setOmega(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{omega.toFixed(1)}</span></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setRunning((r) => !r)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700">
          {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        <button onClick={() => { tRef.current = 0; draw() }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
          <RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default EnergyOscillationSimulation
